// Builds a stored-value -> display-label map for a deal-navbar column so the
// history drawer shows human labels (e.g. "New Prospect") instead of raw enums
// (e.g. "NEW"). Dropdown options live either on the column config
// (`dropdownOptions`, usually an object keyed by value) or as a dynamic array
// (partnerOrgId / podUserId). Owners have neither, so the map comes back
// undefined and the drawer falls back to its own owner/raw resolution.
type OptionLike = { value?: unknown; label?: unknown; name?: unknown };

const add = (m: Record<string, string>, value: unknown, label: unknown) => {
    if (value != null && label != null) m[String(value)] = String(label);
};

const fromSource = (src: unknown): Record<string, string> | undefined => {
    if (!src) return undefined;
    const m: Record<string, string> = {};
    if (Array.isArray(src)) {
        src.forEach((o: OptionLike) => add(m, o?.value, o?.label ?? o?.name));
    } else if (typeof src === 'object') {
        Object.entries(src as Record<string, OptionLike | string>).forEach(([k, o]) =>
            add(m, k, typeof o === 'string' ? o : o?.label ?? o?.name),
        );
    }
    return Object.keys(m).length ? m : undefined;
};

/**
 * @param sources ordered candidate option sources; the first that yields a
 *   non-empty map wins (e.g. [dynamicOptions, column.dropdownOptions]).
 */
export const buildValueLabelMap = (...sources: unknown[]): Record<string, string> | undefined => {
    for (const s of sources) {
        const m = fromSource(s);
        if (m) return m;
    }
    return undefined;
};

/**
 * Resolve a stored history value to its display label using the value->label map.
 * Handles multi-select values that arrive as an array or a comma-joined string by
 * mapping each element and re-joining. Returns null when there's nothing to resolve
 * (no map, or null value) so the caller can fall back to its raw label.
 */
export const resolveHistoryValue = (
    value: string | string[] | null | undefined,
    map: Record<string, string> | undefined,
): string | null => {
    if (!map || value == null) return null;
    const one = (v: string) => map[v] ?? v;
    if (Array.isArray(value)) return value.map((v) => one(String(v))).join(', ') || null;
    if (typeof value === 'string' && value.includes(',')) return value.split(',').map((v) => one(v.trim())).join(', ');
    return map[String(value)] ?? null;
};
